import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApiClient } from '../api/client';
import { Card, CardTitle } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { Table, TableHead, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Pagination } from '../components/ui/Pagination';
import { Badge } from '../components/ui/Badge';
import { Loading } from '../components/ui/Loading';
import { Button } from '../components/ui/Button';
import { Building2, Eye } from 'lucide-react';


const Accounts: React.FC = () => {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['account-stats'],
    queryFn: () => adminApiClient.getAccountStats(),
  });

  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts', page, pageSize],
    queryFn: () => adminApiClient.getAccounts({ page, page_size: pageSize }),
  });


  const formatCents = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };


  if (statsLoading || accountsLoading) {
    return <Loading message="Loading accounts..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Accounts</h1>
        <p className="text-gray-400 mt-1">
          View all accounts on the platform
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Accounts"
            value={stats.total.toLocaleString()}
            icon={Building2}
          />
        </div>
      )}

      {/* Accounts Table */}
      <Card padding="none">
        <div className="p-6 border-b border-gray-700">
          <CardTitle>All Accounts</CardTitle>
        </div>

        {accounts && accounts.data.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell header>Account</TableCell>
                  <TableCell header>Clerk Org</TableCell>
                  <TableCell header>Plan</TableCell>
                  <TableCell header>Members</TableCell>
                  <TableCell header>Repos</TableCell>
                  <TableCell header>Analyses</TableCell>
                  <TableCell header>Balance</TableCell>
                  <TableCell header>Spending</TableCell>
                  <TableCell header>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.data.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>
                      <Link
                        to={`/admin/accounts/${account.id}`}
                        className="hover:text-primary-300"
                      >
                        <div>
                          <span className="font-medium text-white">
                            {account.name || 'Unnamed'}
                          </span>
                          <p className="font-mono text-xs text-gray-500 mt-0.5">
                            {account.id.slice(0, 8)}...
                          </p>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>
                      {account.clerk_org_id ? (
                        <span className="font-mono text-xs text-gray-400">{account.clerk_org_id}</span>
                      ) : (
                        <Badge variant="warning">Unlinked</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {/*
                        Defaults to the neutral variant when absent. Treating
                        "not free" as "paid" made an account whose plan the API
                        omitted render as an empty badge styled like a paying
                        customer — the wrong way to be wrong in an operator list.
                      */}
                      <Badge
                        variant={
                          account.plan_slug && account.plan_slug !== 'free'
                            ? 'pro'
                            : 'default'
                        }
                      >
                        {account.plan_slug ?? '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">{account.member_count}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">{account.repo_count}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">{account.analysis_count}</span>
                    </TableCell>
                    <TableCell>
                      <span className={account.balance_cents < 0 ? 'text-red-400' : 'text-green-400'}>
                        {formatCents(account.balance_cents)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">{formatCents(account.spending_cents)}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Link to={`/admin/accounts/${account.id}`}>
                          <Button variant="ghost" size="sm" title="View Details">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Pagination
              currentPage={accounts.page}
              totalPages={accounts.total_pages}
              totalItems={accounts.total}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </>
        ) : (
          <div className="p-12 text-center text-gray-400">
            No accounts found
          </div>
        )}
      </Card>
    </div>
  );
};

export default Accounts;
